import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axiosInstance, { CheckUserExpiry, getUserDetails,
  handleLegacySessionExpired
} from "../../components/content/axiosConfig";
import Preloader from "../../components/custom/preLoader";
import showErrorToast from "../../components/toast/toast";

const Sampling = () => {
  const [samples, setSamples] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const fisherID = getUserDetails().FisherID;
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [entriesPerPage] = useState(30);
  const [isLoading, setIsLoading] = useState(false);

  const CheckExpiry = useCallback(async () => {
		await CheckUserExpiry();
	 },[]);
  const FetchSamples = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await axiosInstance.get(`/api/FishSampling/GetAllSamples/${fisherID}`, {
        params: { search: searchTerm, page: currentPage, limit: entriesPerPage }
      });
      const body = response.data;
      if (Array.isArray(body)) {
        setSamples(body);
        setTotalCount(body.length);
      } else {
        setSamples(body.data || []);
        setTotalCount(Number(body.total) || 0);
      }
      setIsLoading(false);
     } catch (error) {
      setIsLoading(false);
      if (error.response && error.response.status === 401) {
        const auth = handleLegacySessionExpired(error);
        showErrorToast(auth.message);
      } else {
        const errorMessage = error.response?.data?.message || "Failed to fetch sampling details";
        showErrorToast(errorMessage);
      }

    }
  }, [fisherID, searchTerm, currentPage, entriesPerPage]);

  useEffect(() => {
    CheckExpiry();
  }, [CheckExpiry]);

  useEffect(() => {
    const delay = searchTerm ? 800 : 0;
    const id = setTimeout(() => FetchSamples(), delay);
    return () => clearTimeout(id);
  }, [FetchSamples, searchTerm, currentPage]);

  const indexOfFirstEntry = totalCount === 0 ? 0 : (currentPage - 1) * entriesPerPage + 1;
  const indexOfLastEntry = Math.min(currentPage * entriesPerPage, totalCount);
  const currentEntries = samples;
  const totalPages = Math.ceil(totalCount / entriesPerPage) || 1;

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  return (
    isLoading ? <Preloader /> :
    <div>
      <div className="d-flex align-items-center mb-3">
        <div>
          <ul className="breadcrumb">
            <li className="breadcrumb-item">
              <Link to="/">Home</Link>
            </li>
            <li className="breadcrumb-item Active"> Sampling </li>
          </ul>
          <h1 className="page-header mb-0"> Sampling</h1>
        </div>
        <div className="ms-auto">
          {/* Placeholder for potential future buttons */}
        </div>
      </div>
      <div className="card border-0">
        <div className="tab-content p-3">
          <div className="tab-pane fade show active" id="allTab">
            <div className="input-group mb-3">
              <div className="flex-fill position-relative">
                <div className="input-group">
                  <div
                    className="input-group-text position-absolute top-0 bottom-0 bg-none border-0 start-0"
                    style={{ zIndex: 10 }}
                  >
                    <i className="fa fa-search opacity-5"></i>
                  </div>
                  <input
                    type="text"
                    className="form-control px-35px bg-light"
                    placeholder="Search Sample..."
                    onChange={(e) => {
                      setSearchTerm(e.target.value);
                      setCurrentPage(1);
                    }}
                  />
                </div>
              </div>
            </div>
            <div className="table-responsive mb-3">
              <table className="table table-hover table-panel text-nowrap align-middle mb-0">
                <thead>
                  <tr>
                    <th>Pond Name</th>
                    <th>Pond Fish Type</th>
                    <th>Length</th>
                    <th>Weight</th>
                    <th>Ws</th>
                    <th>Wr</th>
                    <th>Tag Number</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {currentEntries.map((web) => (
                    <tr key={web.FishSamplingID}>
                      <td>{web.PondName}</td>
                      <td>{web.FishTypeName}</td>
                      <td>{web.Length}</td>
                      <td>{web.Weight}</td>
                      <td>{web.Ws}</td>
                      <td>{web.Wr}</td>
                      <td>{web.TagNumber || '-'}</td>
                      <td>
                        {web.Status === "Culled" ? (
                          <span className="badge border border-success text-success px-2 pt-5px pb-5px rounded fs-12px d-inline-flex align-items-center">
                            <i className="fa fa-circle fs-9px fa-fw me-5px"></i>{" "}
                            Culled
                          </span>
                        ) : (
                          <span className="badge border border-danger text-warning px-2 pt-5px pb-5px rounded fs-12px d-inline-flex align-items-center">
                            <i className="fa fa-circle fs-9px fa-fw me-5px"></i>{" "}
                            Release
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="d-md-flex align-items-center">
              <div className="me-md-auto text-md-left text-center mb-2 mb-md-0">
                Showing {indexOfFirstEntry} to {indexOfLastEntry} of {totalCount} entries
              </div>
              <ul className="pagination mb-0 justify-content-center">
                <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
                  <button
                    className="page-link"
                    onClick={() => handlePageChange(currentPage - 1)}
                  >
                    Previous
                  </button>
                </li>
                {[...Array(totalPages)].map((_, index) => (
                  <li
                    key={index}
                    className={`page-item ${index + 1 === currentPage ? "active" : ""}`}
                  >
                    <button
                      className="page-link"
                      onClick={() => handlePageChange(index + 1)}
                    >
                      {index + 1}
                    </button>
                  </li>
                ))}
                <li
                  className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}
                >
                  <button
                    className="page-link"
                    onClick={() => handlePageChange(currentPage + 1)}
                  >
                    Next
                  </button>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Sampling;